import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, Link, MenuItem, Paper,
  Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Badge from "@mui/material/Badge";
import api from "../../api";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { TaskDialog, useRoadmapProgress } from "./RoadmapTodo";
import {
  DEV_CHECKLIST, OWNERS, PLAN_CHANNELS, PLAN_DAYS_PER_WEEK, PLAN_DETAILS, PLAN_SOURCE, PLAN_WEEKS, SOURCE_CALENDARS,
  SOURCE_PERIODS, flattenPlanTasks,
} from "./marketingActionPlanData";

const PURPLE = "#7c3aed";
const DONE_GREEN = "#047857";
const TODAY_BG = "#fef9c3";
const TODAY_BORDER = "#eab308";

export const PLAN_SETTINGS_URL = "admin/analytics/marketing/plan/settings";
export const PLAN_PREFS_KEY = "marketing-action-plan-prefs";
const DEFAULT_PREFS = { view: "week", channel: "all", status: "all" };
const CHANNEL_BY_ID = Object.fromEntries(PLAN_CHANNELS.map((c) => [c.id, c]));
const TOTAL_DAYS = PLAN_WEEKS.length * PLAN_DAYS_PER_WEEK;

// ---------- pure helpers (exported for tests) ----------

export function readPrefs() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(PLAN_PREFS_KEY) || "{}");
    return {
      view: ["week", "day", "month"].includes(raw.view) ? raw.view : DEFAULT_PREFS.view,
      channel: raw.channel === "all" || CHANNEL_BY_ID[raw.channel] ? raw.channel : DEFAULT_PREFS.channel,
      status: ["all", "open", "done"].includes(raw.status) ? raw.status : DEFAULT_PREFS.status,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function parseYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

export function localYmd(date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/** Real date (Y-m-d) of a plan day, or null without a start date. */
export function planDate(startYmd, week, day) {
  const start = parseYmd(startYmd);
  if (start === null) return null;
  const d = new Date(start + ((week - 1) * PLAN_DAYS_PER_WEEK + (day - 1)) * 86400000);
  return d.toISOString().slice(0, 10);
}

/** Where today falls: {state: "none"|"before"|"during"|"after", week, day, daysUntil}. */
export function planPosition(startYmd, todayYmd) {
  const start = parseYmd(startYmd);
  const today = parseYmd(todayYmd);
  if (start === null || today === null) return { state: "none" };
  const idx = Math.round((today - start) / 86400000);
  if (idx < 0) return { state: "before", daysUntil: -idx };
  if (idx >= TOTAL_DAYS) return { state: "after" };
  return { state: "during", week: Math.floor(idx / PLAN_DAYS_PER_WEEK) + 1, day: (idx % PLAN_DAYS_PER_WEEK) + 1 };
}

export function formatYmd(ymd, opts = { weekday: "short", day: "numeric", month: "short" }) {
  const t = parseYmd(ymd);
  return t === null ? "" : new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(new Date(t));
}

export function filterTasks(tasks, { channel = "all", status = "all" }, states = {}) {
  return tasks.filter((t) => {
    if (channel !== "all" && t.channel !== channel) return false;
    const done = !!states[t.id]?.completed;
    if (status === "open" && done) return false;
    if (status === "done" && !done) return false;
    return true;
  });
}

function countDone(tasks, states) {
  return tasks.filter((t) => states[t.id]?.completed).length;
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  document.body.removeChild(area);
}

// ---------- settings (plan start date) ----------

export function usePlanSettings() {
  const [state, setState] = useState({ loading: true, error: "", startDate: null, updatedBy: null, updatedAt: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(PLAN_SETTINGS_URL);
        const d = res.data?.data || {};
        if (alive) setState({ loading: false, error: "", startDate: d.start_date || null, updatedBy: d.updated_by || null, updatedAt: d.updated_at || null });
      } catch (e) {
        if (alive) setState((s) => ({ ...s, loading: false, error: e?.response?.data?.message || "The plan start date could not be loaded." }));
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = useCallback(async (startDate) => {
    const res = await api.put(PLAN_SETTINGS_URL, { start_date: startDate || null });
    const d = res.data?.data || {};
    setState({ loading: false, error: "", startDate: d.start_date || null, updatedBy: d.updated_by || null, updatedAt: d.updated_at || null });
  }, []);

  return { ...state, save };
}

function StartDateDialog({ open, value, onClose, onSave }) {
  const [date, setDate] = useState(value || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(value || "");
      setError("");
    }
  }, [open, value]);

  const submit = async (next) => {
    setSaving(true);
    setError("");
    try {
      await onSave(next);
      onClose();
    } catch (e) {
      const errors = e?.response?.data?.errors;
      setError((errors && Object.values(errors).flat()[0]) || e?.response?.data?.message || "Could not save the start date.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Plan start date</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The real date of Week 1 Day 1. Every day of the plan and today&apos;s highlight follow from it. Shared with every admin.
        </Typography>
        <TextField
          type="date"
          label="Week 1 Day 1"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "plan-start-input" }}
        />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {value && <Button color="inherit" onClick={() => submit(null)} disabled={saving}>Clear</Button>}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => submit(date)}
          disabled={saving || !date}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- small pieces ----------

function ProgressChip({ done, total, testId }) {
  const all = total > 0 && done === total;
  return (
    <Chip
      size="small"
      label={`${done}/${total} done`}
      data-testid={testId}
      sx={{ fontWeight: 700, bgcolor: all ? "#ecfdf5" : "#f5f3ff", color: all ? DONE_GREEN : PURPLE }}
    />
  );
}

function ChannelChip({ channel }) {
  const c = CHANNEL_BY_ID[channel];
  if (!c) return null;
  return <Chip size="small" label={c.label} sx={{ height: 20, fontSize: 11, bgcolor: `${c.color}14`, color: c.color, fontWeight: 600 }} />;
}

function PlanTask({ task, state, disabled, busy, onToggle, onComments, showDetails = true, showWhen = null }) {
  const done = !!state?.completed;
  const count = state?.comment_count || 0;
  return (
    <Box
      data-testid={`plan-task-${task.id}`}
      sx={{ display: "flex", gap: 1, py: 0.75, borderTop: "1px dashed #eef0f4", "&:first-of-type": { borderTop: 0 } }}
    >
      <Checkbox
        size="small"
        checked={done}
        disabled={disabled || busy}
        onChange={(e) => onToggle(task, e.target.checked)}
        icon={<CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: PURPLE }} />}
        checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: DONE_GREEN }} />}
        inputProps={{ "aria-label": `Mark done: ${task.title}` }}
        sx={{ p: 0.5, alignSelf: "flex-start" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}
        >
          {task.title}
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.25 }}>
          {showWhen && <Chip size="small" label={showWhen} sx={{ height: 20, fontSize: 11 }} />}
          <ChannelChip channel={task.channel} />
          {task.owner && <Chip size="small" variant="outlined" label={OWNERS[task.owner] || task.owner} sx={{ height: 20, fontSize: 11 }} />}
          {task.estimate && <Chip size="small" variant="outlined" label={`~${task.estimate} (est.)`} sx={{ height: 20, fontSize: 11 }} />}
          {task.splitByUs && (
            <Tooltip title={`Split into days by us. Plan's weekly task: ${task.fromWeekTask}`}>
              <Chip size="small" label="Split by us" data-testid={`plan-split-${task.id}`} sx={{ height: 20, fontSize: 11, bgcolor: "#f1f5f9" }} />
            </Tooltip>
          )}
        </Stack>
        {task.target && (
          <Typography variant="caption" sx={{ display: "block", color: DONE_GREEN, fontWeight: 600, mt: 0.25 }}>
            Target: {task.target}
          </Typography>
        )}
        {showDetails && task.details?.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.25, color: "text.secondary", "& li": { fontSize: 12.5, lineHeight: 1.45 } }}>
            {task.details.map((d) => <li key={d}>{d}</li>)}
          </Box>
        )}
        {showDetails && task.source && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>Source: {task.source}</Typography>
        )}
        {done && (
          <Typography variant="caption" sx={{ display: "block", color: DONE_GREEN, fontWeight: 700 }} data-testid={`plan-done-${task.id}`}>
            Done{state.completed_by?.name ? ` by ${state.completed_by.name}` : ""}{state.completed_at ? ` · ${formatWhen(state.completed_at)}` : ""}
          </Typography>
        )}
      </Box>
      <Tooltip title="Comments and files">
        <span>
          <IconButton size="small" onClick={() => onComments(task)} disabled={disabled} aria-label={`Comments (${count}): ${task.title}`}>
            <Badge badgeContent={count} color="secondary" max={99}>
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function DayHeader({ week, day, focus, date, isToday, done, total }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Day {day}</Typography>
      {date && <Typography variant="caption" color="text.secondary">{formatYmd(date)}</Typography>}
      {isToday && <Chip size="small" label="Today" data-testid="plan-today-chip" sx={{ height: 20, bgcolor: TODAY_BORDER, color: "#fff", fontWeight: 700 }} />}
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{focus}</Typography>
      <ProgressChip done={done} total={total} testId={`plan-progress-day-w${week}-d${day}`} />
    </Stack>
  );
}

// ---------- other details ----------

function DetailTable({ columns, rows }) {
  return (
    <TableContainer sx={{ border: "1px solid #eef0f4", borderRadius: 1.5, mb: 1.5 }}>
      <Table size="small">
        {columns && (
          <TableHead>
            <TableRow>{columns.map((c) => <TableCell key={c} sx={{ fontWeight: 700, bgcolor: "#fafafa" }}>{c}</TableCell>)}</TableRow>
          </TableHead>
        )}
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.join("|")}>{r.map((cell, i) => <TableCell key={`${i}-${cell}`} sx={{ verticalAlign: "top" }}>{cell}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CopyBlock({ label, text, onCopied }) {
  return (
    <Box sx={{ border: "1px solid #eef0f4", borderRadius: 1.5, p: 1.25, mb: 1.25, bgcolor: "#fcfcfd" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{label}</Typography>
        <Button
          size="small"
          startIcon={<ContentCopyIcon fontSize="small" />}
          aria-label={`Copy: ${label}`}
          onClick={async () => {
            try {
              await copyText(text);
              onCopied(true);
            } catch {
              onCopied(false);
            }
          }}
          sx={{ textTransform: "none" }}
        >
          Copy
        </Button>
      </Stack>
      <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0, color: "text.secondary" }}>{text}</Typography>
    </Box>
  );
}

function DetailBlock({ block, renderChecklist, onCopied }) {
  const caption = block.caption && <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{block.caption}</Typography>;
  switch (block.type) {
    case "p":
      return <Typography variant="body2" sx={{ mb: 1.5 }}>{block.text}</Typography>;
    case "note":
      return <Alert severity="info" sx={{ mb: 1.5 }}>{block.text}</Alert>;
    case "list":
      return (
        <Box sx={{ mb: 1.5 }}>
          {caption}
          <Box component={block.ordered ? "ol" : "ul"} sx={{ m: 0, pl: 2.5, "& li": { fontSize: 14, mb: 0.25 } }}>
            {block.items.map((i) => <li key={i}>{i}</li>)}
          </Box>
        </Box>
      );
    case "table":
      return <Box>{caption}<DetailTable columns={block.columns} rows={block.rows} /></Box>;
    case "copy":
      return <CopyBlock label={block.label} text={block.text} onCopied={onCopied} />;
    case "checklist":
      return <Box sx={{ mb: 1.5 }}>{caption}{renderChecklist()}</Box>;
    case "calendars":
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{SOURCE_PERIODS.title}</Typography>
          <DetailTable columns={SOURCE_PERIODS.columns} rows={SOURCE_PERIODS.rows} />
          {SOURCE_CALENDARS.map((cal) => (
            <Box key={cal.id} sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{cal.title}</Typography>
              <Grid container spacing={1}>
                {cal.weeks.map((w) => (
                  <Grid item xs={12} sm={6} lg={3} key={w.week}>
                    <Box sx={{ border: "1px solid #eef0f4", borderRadius: 1.5, p: 1, height: "100%" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: PURPLE }}>Week {w.week} – {w.title}</Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2, "& li": { fontSize: 12.5 } }}>{w.items.map((i) => <li key={i}>{i}</li>)}</Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      );
    default:
      return null;
  }
}

// ---------- page ----------

export default function MarketingActionPlan() {
  const progress = useRoadmapProgress();
  const settings = usePlanSettings();
  const [prefs, setPrefs] = useState(readPrefs);
  const [dialog, setDialog] = useState({ task: null, mode: "comments" });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [today] = useState(() => localYmd());
  const [selected, setSelected] = useState(null);

  const allTasks = useMemo(() => flattenPlanTasks(), []);
  const disabled = progress.loading || !!progress.error;
  const position = planPosition(settings.startDate, today);
  const isToday = (w, d) => position.state === "during" && position.week === w && position.day === d;
  const current = selected || (position.state === "during" ? { week: position.week, day: position.day } : { week: 1, day: 1 });

  const updatePrefs = (patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch };
      try { window.localStorage.setItem(PLAN_PREFS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };

  const channelOnly = (tasks) => filterTasks(tasks, { channel: prefs.channel }, progress.tasks);
  const visible = (tasks) => filterTasks(tasks, prefs, progress.tasks);

  const onToggle = async (task, completed) => {
    setBusyKey(task.id);
    try {
      await progress.toggle(task.id, completed);
      if (completed) setDialog({ task: { ...task, action: task.title }, mode: "done" });
      else setToast({ severity: "info", text: "Task reopened." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not update the task." });
    } finally {
      setBusyKey(null);
    }
  };

  const onDelete = async (comment) => {
    try {
      await progress.deleteComment(comment);
      setToast({ severity: "success", text: "Comment deleted." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not delete the comment." });
    }
  };

  const onSubmit = async (task, body, files) => {
    await progress.addComment(task.id, body, files);
    setToast({ severity: "success", text: "Comment added." });
  };

  const renderTask = (task, opts = {}) => (
    <PlanTask
      key={task.id}
      task={task}
      state={progress.tasks[task.id]}
      disabled={disabled}
      busy={busyKey === task.id}
      onToggle={onToggle}
      onComments={(t) => setDialog({ task: { ...t, action: t.title }, mode: "comments" })}
      {...opts}
    />
  );

  const dayBox = (week, d, { compactDetails = false } = {}) => {
    const scoped = channelOnly(d.tasks);
    const shown = visible(d.tasks);
    const date = planDate(settings.startDate, week, d.day);
    const today_ = isToday(week, d.day);
    if (!scoped.length) return null;
    return (
      <Box
        key={d.day}
        data-testid={`plan-day-w${week}-d${d.day}`}
        sx={{
          border: `1px solid ${today_ ? TODAY_BORDER : "#eef0f4"}`,
          bgcolor: today_ ? TODAY_BG : "transparent",
          borderRadius: 1.5,
          p: 1,
          mb: 1,
        }}
      >
        <DayHeader week={week} day={d.day} focus={d.focus} date={date} isToday={today_} done={countDone(scoped, progress.tasks)} total={scoped.length} />
        {shown.length ? shown.map((t) => renderTask(t, { showDetails: !compactDetails })) : (
          <Typography variant="caption" color="text.secondary">No tasks match the status filter.</Typography>
        )}
      </Box>
    );
  };

  const todayLabel = (() => {
    if (settings.loading) return "Loading start date…";
    if (position.state === "none") return "No start date yet — set one to map Week 1 Day 1 to a real date.";
    if (position.state === "before") return `Plan starts ${formatYmd(settings.startDate)} (in ${position.daysUntil} day${position.daysUntil === 1 ? "" : "s"}).`;
    if (position.state === "after") return `Plan finished (started ${formatYmd(settings.startDate)}).`;
    return `Today is Week ${position.week} Day ${position.day} (${formatYmd(today)}).`;
  })();

  const weekView = (
    <Grid container spacing={2}>
      {PLAN_WEEKS.map((w) => {
        const scoped = channelOnly(w.days.flatMap((d) => d.tasks));
        const start = planDate(settings.startDate, w.week, 1);
        const end = planDate(settings.startDate, w.week, PLAN_DAYS_PER_WEEK);
        return (
          <Grid item xs={12} lg={6} key={w.week}>
            <Paper sx={{ ...CARD_SX, p: 1.5, height: "100%" }} data-testid={`plan-week-${w.week}`}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5, rowGap: 0.5 }}>
                <Typography sx={{ fontWeight: 700, color: PURPLE }}>Week {w.week} – {w.title}</Typography>
                {start && <Chip size="small" label={`${formatYmd(start, { day: "numeric", month: "short" })} – ${formatYmd(end, { day: "numeric", month: "short" })}`} />}
                <ProgressChip done={countDone(scoped, progress.tasks)} total={scoped.length} testId={`plan-progress-week-${w.week}`} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{w.goal}</Typography>
              {scoped.length ? w.days.map((d) => dayBox(w.week, d, { compactDetails: true })) : (
                <Typography variant="body2" color="text.secondary">No tasks for this channel this week.</Typography>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const stepDay = (delta) => {
    const idx = (current.week - 1) * PLAN_DAYS_PER_WEEK + (current.day - 1) + delta;
    const clamped = Math.max(0, Math.min(TOTAL_DAYS - 1, idx));
    setSelected({ week: Math.floor(clamped / PLAN_DAYS_PER_WEEK) + 1, day: (clamped % PLAN_DAYS_PER_WEEK) + 1 });
  };
  const currentWeek = PLAN_WEEKS.find((w) => w.week === current.week);
  const currentDay = currentWeek?.days.find((d) => d.day === current.day);
  const currentScoped = currentDay ? channelOnly(currentDay.tasks) : [];
  const currentShown = currentDay ? visible(currentDay.tasks) : [];
  const currentDate = planDate(settings.startDate, current.week, current.day);

  const dayView = (
    <Paper sx={{ ...CARD_SX, p: 2 }} data-testid="plan-day-view">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 1 }}>
        <IconButton size="small" aria-label="Previous day" onClick={() => stepDay(-1)} disabled={current.week === 1 && current.day === 1}><ChevronLeftIcon /></IconButton>
        <Typography sx={{ fontWeight: 700, color: PURPLE }} data-testid="plan-day-title">
          Week {current.week} Day {current.day}{currentDate ? ` · ${formatYmd(currentDate, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}` : ""}
        </Typography>
        <IconButton size="small" aria-label="Next day" onClick={() => stepDay(1)} disabled={current.week === PLAN_WEEKS.length && current.day === PLAN_DAYS_PER_WEEK}><ChevronRightIcon /></IconButton>
        {isToday(current.week, current.day) && <Chip size="small" label="Today" sx={{ bgcolor: TODAY_BORDER, color: "#fff", fontWeight: 700 }} />}
        {position.state === "during" && !isToday(current.week, current.day) && (
          <Button size="small" onClick={() => setSelected(null)} sx={{ textTransform: "none" }}>Go to today</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <ProgressChip done={countDone(currentScoped, progress.tasks)} total={currentScoped.length} testId={`plan-progress-day-w${current.week}-d${current.day}`} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {currentWeek?.title} — {currentDay?.focus}
      </Typography>
      {currentShown.length ? currentShown.map((t) => renderTask(t)) : (
        <Typography variant="body2" color="text.secondary">No tasks match the filters for this day.</Typography>
      )}
    </Paper>
  );

  const monthView = (
    <Paper sx={{ ...CARD_SX, p: 1.5 }} data-testid="plan-month-view">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The 30-day plan as a 4-week calendar. Click a day to open it.
      </Typography>
      {PLAN_WEEKS.map((w) => (
        <Box key={w.week} sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: PURPLE }}>Week {w.week} – {w.title}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(7, 1fr)" }, gap: 0.75 }}>
            {w.days.map((d) => {
              const scoped = channelOnly(d.tasks);
              const done = countDone(scoped, progress.tasks);
              const date = planDate(settings.startDate, w.week, d.day);
              const today_ = isToday(w.week, d.day);
              const all = scoped.length > 0 && done === scoped.length;
              return (
                <Box
                  key={d.day}
                  component="button"
                  type="button"
                  data-testid={`plan-month-cell-w${w.week}-d${d.day}`}
                  onClick={() => { setSelected({ week: w.week, day: d.day }); updatePrefs({ view: "day" }); }}
                  sx={{
                    textAlign: "left", cursor: "pointer", font: "inherit", p: 1, borderRadius: 1.5, minHeight: 84,
                    border: `1px solid ${today_ ? TODAY_BORDER : "#eef0f4"}`,
                    bgcolor: today_ ? TODAY_BG : all ? "#ecfdf5" : "#fff",
                    opacity: scoped.length ? 1 : 0.5,
                    "&:hover": { borderColor: PURPLE },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                    Day {d.day}{date ? ` · ${formatYmd(date, { weekday: "short", day: "numeric", month: "short" })}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>{d.focus}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: all ? DONE_GREEN : PURPLE }}>
                    {scoped.length ? `${done}/${scoped.length} done` : "—"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Paper>
  );

  const renderChecklist = () => DEV_CHECKLIST.map((t) => renderTask(t, { showDetails: false }));

  return (
    <AnalyticsPageShell
      title="Marketing action plan"
      subtitle="The 30-day plan from the marketing review as a shared to-do list, plus every supporting detail in one place."
    >
      <Paper sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" data-testid="plan-today-label" sx={{ fontWeight: 600 }}>{todayLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
              Source: <Link href={PLAN_SOURCE.url} target="_blank" rel="noopener noreferrer">{PLAN_SOURCE.title}</Link> (extracted {PLAN_SOURCE.extracted}).
              The plan gives weekly tasks; the split into days, time estimates and the Day 7 reviews are ours (marked &quot;Split by us&quot;).
              {settings.updatedBy?.name ? ` Start date set by ${settings.updatedBy.name}.` : ""}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditCalendarIcon />}
            onClick={() => setStartOpen(true)}
            disabled={settings.loading || !!settings.error}
            sx={{ textTransform: "none", borderColor: PURPLE, color: PURPLE }}
          >
            {settings.startDate ? `Start: ${formatYmd(settings.startDate, { day: "numeric", month: "short", year: "numeric" })}` : "Set plan start date"}
          </Button>
          <ProgressChip done={countDone(allTasks, progress.tasks)} total={allTasks.length} testId="plan-progress-total" />
        </Stack>
        {settings.error && <Alert severity="warning" sx={{ mt: 1 }}>{settings.error}</Alert>}
        {progress.error && <Alert severity="warning" sx={{ mt: 1 }} data-testid="plan-progress-error">{progress.error}</Alert>}
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={prefs.view} onChange={(_, v) => v && updatePrefs({ view: v })} aria-label="Plan view">
          <ToggleButton value="week" sx={{ textTransform: "none", px: 1.5 }}>Week</ToggleButton>
          <ToggleButton value="day" sx={{ textTransform: "none", px: 1.5 }}>Day</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: "none", px: 1.5 }}>Month</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="plan-channel-label">Channel</InputLabel>
          <Select
            labelId="plan-channel-label"
            label="Channel"
            value={prefs.channel}
            onChange={(e) => updatePrefs({ channel: e.target.value })}
            inputProps={{ "data-testid": "plan-channel-select" }}
          >
            <MenuItem value="all">All channels</MenuItem>
            {PLAN_CHANNELS.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={prefs.status} onChange={(_, v) => v && updatePrefs({ status: v })} aria-label="Status filter">
          <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: "none", px: 1.5 }}>Open</ToggleButton>
          <ToggleButton value="done" sx={{ textTransform: "none", px: 1.5 }}>Done</ToggleButton>
        </ToggleButtonGroup>
        {progress.loading && <CircularProgress size={16} />}
        <Typography variant="caption" color="text.secondary">Ticks and comments are shared with every admin.</Typography>
      </Stack>

      <Box sx={{ mb: 3 }}>
        {prefs.view === "week" && weekView}
        {prefs.view === "day" && dayView}
        {prefs.view === "month" && monthView}
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Other details</Typography>
      {PLAN_DETAILS.map((section) => (
        <Accordion key={section.id} disableGutters sx={{ ...CARD_SX, mb: 1, "&:before": { display: "none" } }} data-testid={`plan-details-${section.id}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, flex: 1 }}>{section.title}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: "center" }}>{section.source}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {section.blocks.map((b, i) => (
              <DetailBlock
                key={`${section.id}-${i}`}
                block={b}
                renderChecklist={renderChecklist}
                onCopied={(ok) => setToast(ok ? { severity: "success", text: "Copied to clipboard." } : { severity: "error", text: "Could not copy." })}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      <TaskDialog
        task={dialog.task}
        mode={dialog.mode}
        comments={dialog.task ? progress.comments[dialog.task.id] || [] : []}
        onClose={() => setDialog({ task: null, mode: "comments" })}
        onSubmit={onSubmit}
        onDelete={onDelete}
        onError={(text) => setToast({ severity: "error", text })}
      />
      <StartDateDialog open={startOpen} value={settings.startDate} onClose={() => setStartOpen(false)} onSave={settings.save} />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant="filled" sx={{ width: "100%" }} data-testid="plan-toast">{toast.text}</Alert>
        ) : <span />}
      </Snackbar>
    </AnalyticsPageShell>
  );
}
